// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectsManagement.tsx
// [概要] 営業案件 一覧画面 (index ルート)。
//        親 Layout (SalesProjectsLayout) の Context から useProjectTable の状態を
//        取得する。詳細画面は /salesprojectsmanagement/:id へ遷移する全画面ページとして扱う。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Fab, Popover, Card, Tooltip, IconButton, Chip } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from "../constants/icons";
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';

// 新しい共通コンポーネントとフック
import StandardDataTable, { Column } from '../components/StandardDataTable';
import SalesProjectPhaseChip from '../components/SalesProjectPhaseChip';
import SalesProjectTypeChip from '../components/SalesProjectTypeChip';
import SalesProjectSearchFilters from '../components/SalesProjectSearchFilters';
import { DEFAULT_FILTERS } from '../hooks/useProjectTable';
import { useSalesProjectsTableContext } from '../providers/SalesProjectsTableProvider';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';


// --------------------------------------------------
// 🔽 メインコンポーネント
// --------------------------------------------------
export default function SalesProjectsManagement() {
  const navigate = useNavigate();
  const location = useLocation();

  // --------------------------------------------------
  // 📥 一覧の状態は親 Layout (SalesProjectsLayout) の Context から取得。
  //     詳細ページ ↔ 一覧 を行き来してもデータ・page・検索条件・並び順が保持される。
  // --------------------------------------------------
  const {
    data: projectData,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    sort,
    setPage,
    setPageSize,
    updateFilters,
    handleSort,
    updateSort,
    viewLevel,
    appliedFilterName,
    setAppliedFilterName,
    scrollPositionRef,
  } = useSalesProjectsTableContext();

  // --------------------------------------------------
  // 📜 一覧テーブルのスクロール位置を Layout (Context) 経由で復元
  //     - StandardDataTable から TableContainer の ref を受け取り
  //     - マウント時 (= 詳細から戻ってきた時) に保存値へ復元
  //     - スクロール時に Layout の ref を更新
  //     - アンマウント時にも最終値を保存 (バックアップ)
  // --------------------------------------------------
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const target = scrollPositionRef.current;

    // 復元: まず即時、続いて次のフレームでも再適用 (レイアウト確定後の取りこぼし対策)
    el.scrollTop = target;
    const rafId = requestAnimationFrame(() => {
      if (el.scrollTop !== target) el.scrollTop = target;
    });

    const handleScroll = () => {
      scrollPositionRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      // 退避: 0 で上書きしない (StrictMode の二重マウントや DOM detach 経由の 0 取得を防止)
      if (el.scrollTop > 0) {
        scrollPositionRef.current = el.scrollTop;
      }
      el.removeEventListener('scroll', handleScroll);
    };
  }, [scrollPositionRef]);

  // データ取得が完了して行が描画された後にも復元 (初回ロードや再フェッチ後の対応)
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || loading) return;
    if (scrollPositionRef.current && el.scrollTop !== scrollPositionRef.current) {
      el.scrollTop = scrollPositionRef.current;
    }
  }, [loading, projectData, scrollPositionRef]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSearch = () => {
    setAnchorEl(null);
  };

  const openSearch = Boolean(anchorEl);

  // カラム定義
  const columns = React.useMemo<Column[]>(() => [
    {
      field: 'project_name',
      headerName: '案件名',
      width: 350,
      sticky: true,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, py: 0.5 }}>
          <Typography variant="caption" noWrap sx={{ color: 'text.disabled' }}>
            ID: {row.project_id}
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.primary',
              cursor: 'pointer',
              minWidth: 0,
              transition: 'color 0.15s',
              '.MuiTableRow-root:hover &': { color: '#1976d2' },
            }}
            onClick={() => {
              // 全画面ページへ遷移。戻り時に一覧の URL Query を維持できるよう
              // 現在の URL 検索文字列を state.listSearch に乗せて渡す
              navigate(`/salesprojectsmanagement/${row.project_id}`, {
                state: { listSearch: location.search },
              });
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 700,
                textDecoration: 'none',
                '.MuiTableRow-root:hover &': { textDecoration: 'underline' },
              }}
            >
              {row.project_name}
            </Typography>
            <Icons.OpenInNew
              sx={{
                fontSize: 14,
                opacity: 0,
                transition: 'opacity 0.15s',
                flexShrink: 0,
                '.MuiTableRow-root:hover &': { opacity: 1 },
              }}
            />
          </Box>
        </Box>
      )
    },
    {
      field: 'phase',
      headerName: 'フェーズ',
      width: 180,
      renderCell: (row) => <SalesProjectPhaseChip phase={row.phase} />,
    },
    {
      field: 'project_type',
      headerName: '種別',
      width: 90,
      renderCell: (row) => <SalesProjectTypeChip type={row.project_type} />,
    },
    {
      field: 'main_staff_name',
      headerName: 'メイン',
      width: 220,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
            {row.main_staff_branch_name && (
              <Chip
                label={row.main_staff_branch_name}
                size="small"
                sx={{
                  bgcolor: '#f5f5f5',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  height: 22,
                  borderRadius: '4px',
                }}
              />
            )}
            {row.main_staff_department_name && (
              <Chip
                label={row.main_staff_department_name}
                size="small"
                sx={{
                  bgcolor: '#f5f5f5',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  height: 22,
                  borderRadius: '4px',
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Icons.PersonOutline sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" noWrap>{row.main_staff_name}</Typography>
          </Box>
        </Box>
      ),
    },
    { field: 'client_name', headerName: '取引先', width: 180 },
    { field: 'category_large', headerName: '大項目', width: 180 },
    { field: 'category_medium', headerName: '中項目', width: 180 },
    {
      field: 'expected_order_ym',
      headerName: '受注予定年月',
      width: 140,
      renderCell: (row) => {
        const y = row.expected_order_year;
        const m = row.expected_order_month;
        if (!y && !m) return '-';
        return `${y ? `${y}年` : ''}${m ? `${m}月` : ''}`;
      }
    },
    {
      field: 'order_sales_amount',
      headerName: '売上金額(円)',
      width: 160,
      align: 'right',
      valueFormatter: (value) => {
        if (value == null) return '-';
        return `¥${value.toLocaleString()}`;
      }
    },
    { field: 'issues', headerName: '問題点', width: 200 },
    {
      field: 'remarks',
      headerName: '備考 / 更新日',
      width: 250,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Icons.AccessTime sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ color: row.remarks ? 'text.primary' : 'text.disabled' }}>
              {row.remarks || '備考なし'}
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Icons.CalendarToday sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" noWrap sx={{ color: 'text.disabled' }}>
              最終更新: {row.updated_date || '-'}
            </Typography>
          </Box>
        </Box>
      ),
    },
  ], [navigate, location.search]);

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', px: 2, boxSizing: 'border-box' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
          <Icons.EventAvailable /> 営業案件管理リスト
          {appliedFilterName && (
            <Chip
              label={`適用中: ${appliedFilterName}`}
              color="primary"
              variant="outlined"
              size="small"
              onDelete={() => {
                setAppliedFilterName(null);
                // 検索条件を初期状態にリセット
                updateFilters(DEFAULT_FILTERS);
                updateSort({ field: 'phase', order: 'asc' });
              }}
              sx={{ fontWeight: 'normal' }}
            />
          )}
          {filters.show_long_term && (
            <Chip
              icon={<Icons.Update sx={{ fontSize: '16px !important' }} />}
              label="中長期フィルタ ON"
              color="primary"
              variant="outlined"
              size="small"
              onDelete={() => updateFilters({ ...filters, show_long_term: false })}
              sx={{ fontWeight: 'normal' }}
            />
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {/* 検索ボタン */}
          <Fab
            variant="extended"
            onClick={handleOpenSearch}
            sx={[STANDARD_FAB_CTA_SX, { mx: 4 }]}
          >
            <Icons.FilterAltOutlined />
            案件検索
          </Fab>

          {/* 新規登録ボタン */}
          <Fab
            variant="extended"
            onClick={() => navigate('/salesprojectscreate')}
            sx={STANDARD_FAB_CTA_SX}
          >
            <Icons.Add />
            案件の新規登録
          </Fab>
        </Box>
      </Box>

      {/* 検索ポップオーバー (Popover + Card形式) */}
      <Popover
        open={openSearch}
        anchorEl={anchorEl}
        onClose={handleCloseSearch}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #edf2f7'
          }
        }}
      >
        <Card sx={{ width: '650px', border: 'none', boxShadow: 'none' }}>
          <Box sx={{ p: 1, borderBottom: '1px solid #eee', bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>
              案件を検索・絞り込み
            </Typography>
            <Tooltip title="保存済み条件一覧">
              <IconButton size="small" onClick={() => { handleCloseSearch(); navigate('/salesprojectsfilters'); }}>
                <Icons.Bookmarks sx={{ fontSize: 18, color: '#64748b' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <SalesProjectSearchFilters
            filters={filters}
            viewLevel={viewLevel}
            sort={sort}
            onSortChange={(s) => {
              updateSort(s);
              setAppliedFilterName(null); // ソート変更時もクリア（または維持するか検討）
            }}
            onSearch={(newFilters) => {
              updateFilters(newFilters);
              handleCloseSearch();
              setAppliedFilterName(null); // 手動検索時は名前をクリア
            }}
          />
        </Card>
      </Popover>

      <Box sx={{ ...TABLE_FILL_SX, mt: 1 }}>
        <StandardDataTable
          rows={projectData}
          columns={columns}
          loading={loading}
          loadingMessage="案件データを取得中"
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          sortField={sort.field}
          sortOrder={sort.order}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSortChange={handleSort}
          scrollContainerRef={tableScrollRef}
        />
      </Box>
    </Box >
  );
}
